import { Session, SessionOnConnectionBrokenParams, SessionOnConnectionEstablishedParams, SessionOnEndedParams, SessionOnMessageParams, SessionStatus, SessionStatusHandler } from './Session';
import 'amazon-connect-chatjs';
import * as api from '@amzn/ring-cs-chatbot-service-model';
import { SubscriptionContainer } from '../../utils/SubscriptionContainer';

export type ConnectSessionStorageProperties = {
    contactId: string;
    participantId: string;
    participantToken: string;
    attributes: Record<string, string>;
};

export class ConnectSession implements Session {
    private readonly participantIdRoleMap: Map<string, string>;
    private readonly statusContainer: SubscriptionContainer<SessionStatus>;
    private readonly customerChatSession: connect.CustomerChatSession;
    private readonly attributes: Record<string, string>;

    private skipNextConnectionGainEvent: boolean;

    private onConnectionEstablishedHandler?: () => void;
    private onConnectionBrokenHandler?: () => void;
    private onMessageHandler?: (message: api.ResponseMessage) => void;
    private onEndedHandler?: () => void;

    constructor (properties: ConnectSessionStorageProperties) {
        this.participantIdRoleMap = new Map();
        this.statusContainer = new SubscriptionContainer<SessionStatus>(SessionStatus.CREATED);

        connect.ChatSession.setGlobalConfig({
            region: 'us-east-1',
        });

        this.customerChatSession = connect.ChatSession.create({
            type: connect.ChatSession.SessionTypes.CUSTOMER,
            chatDetails: {
                contactId: properties.contactId,
                participantId: properties.participantId,
                participantToken: properties.participantToken,
            },
        });

        this.attributes = properties.attributes;

        this.skipNextConnectionGainEvent = false;
        this.customerChatSession.onConnectionEstablished((...eventData: any[]) => {
            if (this.statusContainer.value === SessionStatus.ENDED) return;

            /*
             * just follow the same strategy as
             * https://code.amazon.com/packages/AmazonConnectChatInterface/blobs/6259311f480eb7bebda45b7f098660a770bf4969/--/src/components/Chat/ChatSession.js#L462
             */

            //prevent loadLatestTranscript from being called twice.
            if (this.skipNextConnectionGainEvent) {
                this.skipNextConnectionGainEvent = false;
                return;
            }
            //initial connection has been established; skip the next request bec of websocket-connection gain.
            if (eventData[0]?.connectSuccess) {
                this.skipNextConnectionGainEvent = true;
            }

            this.statusContainer.setValue(SessionStatus.ESTABLISHED);
            this.onConnectionEstablishedHandler?.();
        });

        this.customerChatSession.onConnectionBroken(() => {
            if (this.statusContainer.value === SessionStatus.ENDED) return;

            this.statusContainer.setValue(SessionStatus.BROKEN);
            this.onConnectionBrokenHandler?.();
        });

        this.customerChatSession.onEnded(() => {
            console.log('onEnded');
            this.internalEnd();
        });

        const messageHandler = (data: connect.ChatEventData) => {
            this.readParticipants(data);
            // TODO: Verify behavior with empty contactId when this code is put into use.
            const message = this.convertMessage(data, data.ContactId || '');

            if (message) {
                this.onMessageHandler?.(message);
            }
        };

        this.customerChatSession.onMessage(event => messageHandler(event.data));
        this.customerChatSession.onTyping(event => messageHandler(event.data));
        this.customerChatSession.onReadReceipt(event => messageHandler(event.data));
        this.customerChatSession.onDeliveredReceipt(event => messageHandler(event.data));
        this.customerChatSession.onParticipantIdle(event => messageHandler(event.data));
        this.customerChatSession.onParticipantReturned(event => messageHandler(event.data));
    }

    private readParticipants = (event: connect.ChatEventData | connect.ChatTranscriptItem) => {
        if (event.ParticipantId && event.ParticipantRole) {
            this.participantIdRoleMap.set(event.ParticipantId, event.ParticipantRole);
        }
    }

    private convertMessage = (event: connect.ChatEventData | connect.ChatTranscriptItem, contactId: string): api.ResponseMessage | undefined => {
        const metadata = (event as any).MessageMetadata;

        if (['MESSAGE', 'EVENT'].includes(event.Type) && event.ContentType) {
            const message: api.ResponseMessage = {
                sessionId: contactId,
                type: event.Type as api.MessageType,
                messageId: event.Id,
                contentType: event.ContentType,
                content: event.Content,
                displayName: event.DisplayName || '',
                participantRole: (event.ParticipantRole as api.MessageParticipantRole) || api.MessageParticipantRole.SYSTEM,
                absoluteTime: new Date(event.AbsoluteTime).getTime(),
                ...(metadata ? {
                    messageMetadata: {
                        messageId: metadata.MessageId,
                        receipts: metadata.Receipts.map((receipt: any) => ({
                            deliveredTimestamp: new Date(receipt.DeliveredTimestamp).getTime(),
                            readTimestamp: new Date(receipt.ReadTimestamp).getTime(),
                            recipientParticipantRole: this.participantIdRoleMap.get(receipt.RecipientParticipantId),
                        })),
                    },
                } : {}),
            };
            return message;
        } else if (['MESSAGEMETADATA'].includes(event.Type) && event.ContentType) {
            const message: api.ResponseMessage = {
                sessionId: contactId,
                type: event.Type as api.MessageType,
                messageId: event.Id,
                contentType: event.ContentType,
                displayName: 'Connect',
                participantRole: api.MessageParticipantRole.SYSTEM,
                absoluteTime: new Date(event.AbsoluteTime).getTime(),
                ...(metadata ? {
                    messageMetadata: {
                        messageId: metadata.MessageId,
                        receipts: metadata.Receipts.map((receipt: any) => ({
                            deliveredTimestamp: new Date(receipt.DeliveredTimestamp).getTime(),
                            readTimestamp: new Date(receipt.ReadTimestamp).getTime(),
                            recipientParticipantRole: this.participantIdRoleMap.get(receipt.RecipientParticipantId),
                        })),
                    },
                } : {}),
            };
            return message;
        } else {
            return undefined;
        }
    };

    private internalEnd = () => {
        const endedHandler = this.onEndedHandler;

        this.clear();

        // mark the session ended
        this.statusContainer.setValue(SessionStatus.ENDED);
        endedHandler?.();
    };

    clear = () => {
        // shut everything down
        this.onConnectionEstablishedHandler = undefined;
        this.onConnectionBrokenHandler = undefined;
        this.onMessageHandler = undefined;
        this.onEndedHandler = undefined;
    };

    connect = async () => {
        this.statusContainer.setValue(SessionStatus.CONNECTING);

        try {
            await this.customerChatSession.connect();
        } catch (err) {
            this.internalEnd();
            throw err;
        }
    };

    getAttributes = (): Record<string, string> => {
        return this.attributes;
    };

    sendMessage = async (params: api.SendMessageRequest) => {
        const {
            message,
        } = params;

        switch (message.type) {
            case api.MessageType.MESSAGE:
                const sendMessageArgs: connect.SendMessageArgs = {
                    contentType: message.contentType,
                    message: message.content,
                };

                const messageResult = await this.customerChatSession.sendMessage(sendMessageArgs);

                if (!messageResult.data) {
                    throw new Error(`No response from sendMessage: ${messageResult.error}`);
                }

                break;
            case api.MessageType.EVENT:
                const sendEventArgs: connect.SendEventArgs = {
                    contentType: message.contentType as any,
                    content: 'content' in message ? message.content : undefined,
                };

                const eventResult = await this.customerChatSession.sendEvent(sendEventArgs);

                if (!eventResult.data) {
                    throw new Error(`No response from sendEvent: ${eventResult.error}`);
                }

                break;
        }

        return {}; // everything comes back on the websocket
    };

    transcript = async (params: api.TranscriptRequest) => {
        const transcriptResult = await this.customerChatSession.getTranscript({
            scanDirection: params.scanDirection || api.TranscriptScanDirection.BACKWARD,
            sortOrder: params.sortOrder || 'DESCENDING',
            maxResults: params.maxResults || 15,
            nextToken: params.nextToken,
        });

        if (!transcriptResult.data) {
            throw new Error(`No response from transcript: ${transcriptResult.error}`);
        }

        transcriptResult.data.Transcript
            .forEach(message => this.readParticipants(message));

        const initialContactId = transcriptResult.data.InitialContactId;
        const messages = transcriptResult.data.Transcript
            .map(message => this.convertMessage(message, initialContactId))
            .filter(message => !!message) as api.ResponseMessage[];

        const response: api.TranscriptResponse = {
            sessionId: initialContactId,
            nextToken: transcriptResult.data.NextToken,
            transcript: messages,
        };

        return response;
    };

    end = async () => {
        try {
            this.customerChatSession.disconnectParticipant();
        } finally {
            this.internalEnd();
        }
    };

    onStatusUpdate = (handler: SessionStatusHandler) => {
        return this.statusContainer.onUpdate(handler);
    };

    onConnectionEstablished = (params: SessionOnConnectionEstablishedParams) => {
        this.onConnectionEstablishedHandler = params.handler;
    };

    onConnectionBroken = (params: SessionOnConnectionBrokenParams) => {
        this.onConnectionBrokenHandler = params.handler;
    };

    onMessage = (params: SessionOnMessageParams) => {
        this.onMessageHandler = params.handler;
    };

    onEnded = (params: SessionOnEndedParams) => {
        this.onEndedHandler = params.handler;
    };
}
